import React, { useState } from 'react';
import { Share2 } from 'lucide-react';
import './ForkModal.css';

interface ForkModalProps {
  planTitle: string;
  destination: string;
  creatorName: string;
  durationDays: number;
  onConfirm: (startDate: string) => Promise<{ id: number }>;
  onGoToPlan: (itineraryId: number) => void;
  onSharePlan: (itineraryId: number) => void;
  onClose: () => void;
}

/**
 * `count` weekly start dates from today, as LOCAL calendar days.
 *
 * Not `toISOString()`: that renders the UTC day, so west of Greenwich in the
 * evening the first option — the one labelled as today — is already tomorrow,
 * and every option after it is off by one. The label right beside it is parsed
 * as local (`formatDateLabel`), so the two disagreed about the same string.
 */
function getWeeklyOptions(count: number): string[] {
  const today = new Date();
  const options: string[] = [];
  const pad = (n: number) => String(n).padStart(2, '0');
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i * 7);
    options.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  }
  return options;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * No 'personalize' step. It asked for pace, preferences and notes, posted them
 * to `personalize_itinerary`, and the endpoint stored the answers next to a
 * `# TODO` where the AI call would go — nothing about the plan ever changed.
 * Tailoring is a conversation (EXPLORE_PLAN_NOW_ALIGNMENT_PROPOSAL.md §3 Phase 1.4), so the modal ends where
 * it has something true to say: the plan is saved, here it is.
 */
type Step = 'confirm' | 'dates' | 'saving' | 'success';

const ForkModal: React.FC<ForkModalProps> = ({
  planTitle,
  creatorName,
  durationDays,
  onConfirm,
  onGoToPlan,
  onSharePlan,
  onClose,
}) => {
  const [step, setStep] = useState<Step>('confirm');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [resultId, setResultId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const weeklyDates = getWeeklyOptions(6);

  const handleDatePick = async (date: string, label: string) => {
    setSelectedDate(date);
    setSelectedLabel(label);
    setStep('saving');
    setError('');
    try {
      const result = await onConfirm(date);
      setResultId(result.id);
      setStep('success');
    } catch {
      setError('Something went wrong. Please try again.');
      setStep('dates');
    }
  };

  return (
    <div className="fork-modal-backdrop" onClick={onClose}>
      <div className="fork-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fork-modal-handle" />

        {/* Step: Confirm */}
        {step === 'confirm' && (
          <>
            <h3 className="fork-modal-title">Save &ldquo;{planTitle}&rdquo;</h3>
            <p className="fork-modal-creator">by {creatorName}</p>
            <p className="fork-modal-desc">
              This creates your own copy. {creatorName} gets credit as the original creator.
            </p>
            <button className="fork-modal-primary-btn" onClick={() => setStep('dates')}>
              Save to my plans
            </button>
            <button className="fork-modal-dismiss" onClick={onClose}>
              Cancel
            </button>
          </>
        )}

        {/* Step: Date picker */}
        {step === 'dates' && (
          <>
            <h3 className="fork-modal-title">When are you planning this?</h3>
            <p className="fork-modal-subtitle">
              Pick a start date for your {durationDays} day{durationDays !== 1 ? 's' : ''} plan.
            </p>
            <div className="fork-modal-date-list">
              {weeklyDates.map((d) => {
                const label = formatDateLabel(d);
                return (
                  <button
                    key={d}
                    className={`fork-modal-date-option ${selectedDate === d ? 'fork-modal-date-option--active' : ''}`}
                    onClick={() => handleDatePick(d, label)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {error && <p className="fork-modal-error">{error}</p>}
            <button className="fork-modal-dismiss" onClick={() => setStep('confirm')}>
              Back
            </button>
          </>
        )}

        {/* Step: Saving */}
        {step === 'saving' && (
          <div className="fork-modal-success" style={{ padding: '40px 0' }}>
            <p className="fork-modal-saving">Saving your plan...</p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && resultId && (
          <div className="fork-modal-success">
            <div className="fork-modal-success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-sage)" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 className="fork-modal-title">Plan saved!</h3>
            <p className="fork-modal-attribution">
              Based on a plan by <strong>{creatorName}</strong>
            </p>
            <p className="fork-modal-date-confirm">Starting {selectedLabel}</p>

            <button className="fork-modal-primary-btn" onClick={() => onGoToPlan(resultId)}>
              Go to my plan
            </button>
            <button className="fork-modal-share-btn" onClick={() => onSharePlan(resultId)}>
              <Share2 size={14} /> Share your version
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForkModal;
